import { Link } from "react-router-dom";
import { useMidday } from "@/lib/useRhythm";

/**
 * Midday — a single quiet panel surfaced only during the 12-15 local window.
 * Four optional snippets composed server-side. Renders nothing when none
 * are available (so the surface stays empty by absence).
 *
 * Intentionally one panel with four typographic sub-sections rather than
 * four separate cards — keeps the moment a single read, not a dashboard.
 */
export function MiddayMoment() {
  const { data, isLoading } = useMidday();
  if (isLoading || !data) return null;
  const { insight, opportunity, prompt, continuity } = data;
  if (!insight && !opportunity && !continuity) return null;

  return (
    <section className="mt-12 first:mt-0">
      <header className="mb-4">
        <p className="label-eyebrow">Midday</p>
        <h2 className="mt-1.5 font-display text-xl tracking-tight">A light interruption</h2>
      </header>
      <div className="panel p-7 space-y-7">
        {insight && (
          <Block label="One thing to read">
            <a
              href={insight.url}
              target="_blank"
              rel="noreferrer"
              className="font-display text-base leading-snug hover:text-gold-glow transition-colors block"
            >
              {insight.title}
            </a>
            <p className="mt-2 text-sm text-whisper leading-relaxed">{insight.summary}</p>
            <p className="mt-2 text-[10px] uppercase tracking-[0.18em] text-muted">
              via {insight.source_name}
            </p>
          </Block>
        )}

        {opportunity && (
          <Block label="An opportunity in your library">
            <p className="font-display text-base leading-snug">{opportunity.title}</p>
            {opportunity.deadline_at && (
              <p className="mt-1 text-[10px] uppercase tracking-[0.18em] text-gold-glow/80">
                deadline {new Date(opportunity.deadline_at).toLocaleDateString(undefined, { month: "short", day: "numeric" })}
              </p>
            )}
          </Block>
        )}

        {prompt && (
          <Block label="A question">
            <p className="text-base text-fog italic leading-relaxed">{prompt}</p>
          </Block>
        )}

        {continuity && (
          <Block label="Continuity">
            <Link
              to={continuity.link}
              className="text-sm text-whisper hover:text-fog transition-colors leading-relaxed"
            >
              {continuity.message}
            </Link>
          </Block>
        )}
      </div>
    </section>
  );
}

function Block({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <p className="label-eyebrow text-gold-glow/80 mb-2">{label}</p>
      {children}
    </div>
  );
}
